import Track from "../core/instrumentation";
import { Site } from "../resources/modelTypes";
import { useLDClient } from "launchdarkly-react-client-sdk";
import noop from "lodash/noop";
import React, { createContext, ReactNode, useCallback } from "react";

const isProduction = process.env.NODE_ENV === "production";

// Used for the multi-site dashboard rather than wrapping each site card in a track
// context
export const useSiteTrack = (site: Site) =>
  useCallback(
    (payload: Record<string, any>) => {
      if (isProduction) {
        Track.emit({ ...payload, site_id: site.id, site_type: site.siteType });
      } else {
        console.info({
          ...payload,
          site_id: site.id,
          site_type: site.siteType,
        });
      }
    },
    [site.id, site.siteType],
  );

export type TrackContextValue = (
  payload: Record<string, any>,
  sendToLD?: boolean,
) => void;

export const TrackContext = createContext<TrackContextValue>(noop);

export type TrackContextProviderProps = {
  children: ReactNode;
  site: Site;
};

export const TrackContextProvider = ({
  children,
  site,
}: TrackContextProviderProps) => {
  const ldClient = useLDClient();

  const emitSiteEvent = useCallback(
    (payload: Record<string, any>, sendToLD?: boolean) => {
      if (isProduction) {
        Track.emit({
          ...payload,
          site_id: site.id,
          site_type: site.siteType,
        });
      } else {
        console.info({
          ...payload,
          site_id: site.id,
          site_type: site.siteType,
        });
      }
      if (sendToLD) {
        ldClient?.track(payload.event);
      }
    },
    [ldClient, site.id, site.siteType],
  );

  return (
    <TrackContext.Provider value={emitSiteEvent}>
      {children}
    </TrackContext.Provider>
  );
};
